import { APRIL, AUG, JULY, JUNE, MAY, NOV, OCT, SEPT } from '../constants'
import {
  add,
  isWithinInterval as dateFnsIsWithinInterval,
  getDay,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isFuture,
  isMonday,
  isSameDay,
  isSaturday,
  isSunday,
  isThursday,
  isToday,
  isTomorrow,
  isTuesday,
  isWednesday,
  isWeekend,
  parseISO,
  set,
  sub,
} from 'date-fns'
import { formatDateTime, parseDateTime } from './dateTimeUtil'

import { CURRENT_YEAR } from './commonUtil'
import { EmailLink } from '../components/common/EmailLink'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import axios from '../axios/axios'
import { notifyError } from './notifyUtil'

// These need to match the server.
const CANCEL_BOOKING_GRACE_PERIOD = 15
const EDIT_BOOKING_GRACE_PERIOD = 15
const BOOKABLE_GRACE_PERIOD = 15
const LAST_MINUTE_BOOKING_PERIOD = 60 * 2 // 2 hours
const MAX_FUTURE_DAYS = 7

const FAR_FUTURE = new Date(2099, 1, 1)

const BOOKING_RULES_VIEWED_KEY = 'bookingRulesViewed-20250722'
export const setBookingRulesViewed = () => localStorage.setItem(BOOKING_RULES_VIEWED_KEY, 'yes')
export const bookingRulesViewed = () => localStorage.getItem(BOOKING_RULES_VIEWED_KEY) === 'yes'

export const BOOKING_VIEW_MODE = { DAY: 'DAY', WEEK: 'WEEK' }

export const getDayHeading = (date, large = true) =>
  isToday(date) ? 'Today' : formatDateTime(date, large ? 'EEEE' : 'EEE')

export const getDisplayDate = (date, showFriendlyName = true) => {
  const dateStr = formatDateTime(date, 'EEEE MMMM d')
  let displayDate
  if (showFriendlyName && isToday(date)) {
    displayDate = `${dateStr} (Today)`
  } else if (showFriendlyName && isTomorrow(date)) {
    displayDate = `${dateStr} (Tomorrow)`
  } else {
    displayDate = dateStr
  }
  return displayDate
}

export const getCourtsText = (booking) => {
  if (!booking) {
    return ''
  }
  const courts = booking.courts
  const count = _.size(courts)
  if (count === 1) {
    const c = courts[0]
    return c.label || `Court ${c.id}`
  } else if (count > 1) {
    return (
      'Courts ' +
      _.join(
        _.map(booking.courts, (c) => c.id),
        ', ',
      )
    )
  } else {
    return ''
  }
}

/**
 * Returns the player's first name and last initial if available, falling back to just first or last or username.
 */
export const getPlayerFirstNameAndLastInitial = (player) => {
  var result
  const firstName = player?.firstName
  const lastName = player?.lastName ? player.lastName.substring(0, 1) + '.' : null
  if (!firstName && !lastName) {
    result = player?.username
  } else {
    result = (firstName || '') + ' ' + (lastName || '')
  }
  if (_.isEmpty(_.trim(result))) {
    result = player?.username
  }
  return result
}

const getPlayerText = (booking, playerIndex) => {
  const guestName = booking[`guest${playerIndex}`]
  const player = booking[`player${playerIndex}`]
  const playerName = getPlayerFirstNameAndLastInitial(player) ?? guestName
  const isGuest = !!guestName
  if (!playerName) {
    return null
  }
  return isGuest ? `${playerName} (guest)` : playerName
}

export const getPlayersText = (booking) => {
  if (!booking) {
    return ''
  }
  const creatorName = getPlayerFirstNameAndLastInitial(booking.createdByUser)
  const playerNames = [
    getPlayerText(booking, 1),
    getPlayerText(booking, 2),
    getPlayerText(booking, 3),
    getPlayerText(booking, 4),
  ]
  // Exclude empty names/players, and the name of the person that booked the court.
  const names = _.reject(playerNames, (playerText) => _.isNil(playerText) || (playerText && playerText === creatorName))
  return _.join(names, ', ')
}

const getTimeslot = (hour, minute = 0, duration = 60) => {
  const time = set(new Date(), { hours: hour, minutes: minute, seconds: 0, milliseconds: 0 })
  return { startTime: time, duration }
}

const DEFAULT_WEEKDAY = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]
const DEFAULT_WEEKEND = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]

const getHoursAndMinutes = (hours) => {
  const h = Math.floor(hours)
  const m = (hours % 1).toFixed(1) * 60
  return [h, m]
}

const getTimeslots = (hours) => {
  const result = []
  for (const hour of hours) {
    const [h, m] = getHoursAndMinutes(hour)
    result.push(getTimeslot(h, m))
  }
  return result
}

const isWithinInterval = (date, interval, atEndOfDay = true) => {
  const { start, end } = interval ?? {}
  const endAtEndOfDay = atEndOfDay ? set(end, { hours: 23, minutes: 59, seconds: 59 }) : end
  return dateFnsIsWithinInterval(date, { start, end: endAtEndOfDay })
}

const JUNIOR_LESSONS = { start: new Date(CURRENT_YEAR, MAY, 13), end: new Date(CURRENT_YEAR, JUNE, 27) }
const JUNIOR_CAMPS = { start: new Date(CURRENT_YEAR, JUNE, 30), end: new Date(CURRENT_YEAR, AUG, 23) }
const JUNIOR_ADV_CAMPS = { start: new Date(CURRENT_YEAR, JULY, 2), end: new Date(CURRENT_YEAR, AUG, 25) }
const JUNIOR_DROPINS = { start: new Date(CURRENT_YEAR, JUNE, 23), end: new Date(CURRENT_YEAR, SEPT, 14) }
const ADULT_DOUBLES = { start: new Date(CURRENT_YEAR, MAY, 22), end: new Date(CURRENT_YEAR, SEPT, 29) }
const ADULT_LESSONS = { start: new Date(CURRENT_YEAR, MAY, 14), end: new Date(CURRENT_YEAR, SEPT, 15) }
const ADULT_DROPINS = { start: new Date(CURRENT_YEAR, MAY, 7), end: new Date(CURRENT_YEAR, OCT, 2) }

export const isBallMachineAvailable = (bookingTime) => {
  if (isBefore(bookingTime, new Date(CURRENT_YEAR, JULY, 7))) {
    return false
  }
  if (isDate(bookingTime, AUG, 4)) {
    return false
  }
  const hour = getHours(bookingTime)
  return (isWeekend(bookingTime) && hour >= 15 && hour < 17) || (!isWeekend(bookingTime) && hour >= 13 && hour < 17)
}
export const isBallMachineBooked = async (bookingTime) => {
  try {
    const result = await axios.get(`/all/bookings/${formatDateTime(bookingTime, 'yyyyMMdd')}`)
    const bookings = result?.data

    const sameDayAndTime = (time1, time2) => {
      const format = 'yyyMMdd:HH:mm:ss'
      const one = formatDateTime(parseDateTime(time1), format)
      const two = formatDateTime(time2, format)
      return one === two
    }

    const existingBallMachineBooking = _.find(
      bookings,
      (booking) => booking.bookingType === 'Ball-Machine' && sameDayAndTime(booking.startTime, bookingTime),
    )

    return !_.isNil(existingBallMachineBooking)
  } catch (e) {
    notifyError('error', e)
  }
}

const isJuniorLessonsDay = (date) =>
  isWithinInterval(date, JUNIOR_LESSONS) && (isTuesday(date) || isWednesday(date) || isThursday(date))
const isJuniorCampDay = (date) =>
  isWithinInterval(date, JUNIOR_CAMPS) &&
  !isSaturday(date) &&
  !isSunday(date) &&
  !isSameDay(date, new Date(CURRENT_YEAR, JULY, 1)) &&
  !isSameDay(date, new Date(CURRENT_YEAR, AUG, 4))
const isSwingAndSplashDay = (date) =>
  isDate(date, JULY, 3) ||
  isDate(date, JULY, 8) ||
  isDate(date, JULY, 10) ||
  isDate(date, JULY, 15) ||
  isDate(date, JULY, 17) ||
  isDate(date, JULY, 22) ||
  isDate(date, JULY, 24) ||
  isDate(date, JULY, 29) ||
  isDate(date, JULY, 31) ||
  isDate(date, AUG, 5) ||
  isDate(date, AUG, 7)
const isHVNADay = (date) => isDate(date, JULY, 9) || isDate(date, JULY, 23) || isDate(date, AUG, 13)
const isJuniorAdvCampDay = (date) => isWithinInterval(date, JUNIOR_ADV_CAMPS) && isWednesday(date)
const isAdultDoublesDay = (date) =>
  isWithinInterval(date, ADULT_DOUBLES) && (isSunday(date) || isMonday(date) || isThursday(date))

const isJuniorDropInDay = (date) => false // isWithinInterval(date, JUNIOR_DROPINS) && isThursday(date)

const isAdultLessonsDay = (date) => isWithinInterval(date, ADULT_LESSONS) && isWednesday(date) && !isDate(date, AUG, 30)
const isAdultDropInDay = (date) =>
  isWithinInterval(date, ADULT_DROPINS) &&
  isSunday(date) &&
  !isDate(date, AUG, 20) &&
  !isDate(date, AUG, 27) &&
  !isDate(date, SEPT, 3) &&
  !isDate(date, SEPT, 10)

const isInterurbanHomeDate = (date) =>
  isDate(date, MAY, 27) ||
  isDate(date, JUNE, 3) ||
  isDate(date, JUNE, 17) ||
  isDate(date, JUNE, 24) ||
  isDate(date, JULY, 22) ||
  isDate(date, AUG, 12)

const removeTimeslot = (timeslots, hour, min = 0) => {
  if (_.isArray(hour)) {
    hour.forEach((h) => removeTimeslot(timeslots, h))
  } else {
    _.remove(timeslots, (t) => getHours(t.startTime) === hour && getMinutes(t.startTime) === min)
  }
}
const addTimeslot = (timeslots, timeslot) => timeslots.push(timeslot)

const replaceTimeslot = (timeslots, startTime, endTime, newTimeslot) => {
  removeTimeslot(timeslots, _.range(startTime, endTime))
  addTimeslot(timeslots, {
    ...getTimeslot(startTime, 0, (endTime - startTime) * 60),
    ...newTimeslot,
  })
}

const isDate = (date, month, day) => isSameDay(date, new Date(CURRENT_YEAR, month, day))

const adjustTimeslots = (date, timeslots) => {
  // Adjust (or replace) timeslots for a given date.

  if (isAfter(date, new Date(CURRENT_YEAR, NOV, 15))) {
    timeslots = []
    addTimeslot(timeslots, {
      ...getTimeslot(8, 0),
      message: 'Courts closed for the season',
      heightOverride: 400,
    })
  }

  // Closed for court cleaning.
  if (isDate(date, APRIL, 30)) {
    replaceTimeslot(timeslots, 8, 16, {
      message:
        'Courts closed due to court cleaning. Depending on weather conditions, the courts may still be wet afterwards.  Check the homepage for updates.',
      heightOverride: 300,
    })
  }

  if (isDate(date, APRIL, 23) || isDate(date, APRIL, 24)) {
    removeTimeslot(timeslots, _.range(18, 22))
    addTimeslot(timeslots, { ...getTimeslot(18, 0, 30) })
    addTimeslot(timeslots, { ...getTimeslot(20, 30, 90) })
    addTimeslot(timeslots, {
      ...getTimeslot(18, 30, 120),
      message: <Link to="/adult-doubles">Adult doubles assessments</Link>,
    })
  }

  if (isDate(date, MAY, 3)) {
    removeTimeslot(timeslots, _.range(9, 12))
    addTimeslot(timeslots, { ...getTimeslot(9, 0, 60 * 3), message: 'Meet and Greet' })
    // addTimeslot(timeslots, { ...getTimeslot(13, 0, 60 * 1), message: 'Free days in May (adults)' })
  }
  if (isDate(date, MAY, 10) || isDate(date, MAY, 24)) {
    removeTimeslot(timeslots, _.range(10, 12))
    addTimeslot(timeslots, { ...getTimeslot(10, 0, 60 * 2), message: 'Free days in May' })
    // addTimeslot(timeslots, { ...getTimeslot(13, 0, 60 * 1), message: 'Free days in May (adults)' })
  }
  if (isDate(date, JUNE, 7)) {
    removeTimeslot(timeslots, _.range(9, 12))
    addTimeslot(timeslots, { ...getTimeslot(9, 0, 60 * 3), message: 'Season Opening Tournament' })
  }
  // if (isDate(date, JUNE, 21)) {
  //   removeTimeslot(timeslots, _.range(9, 15))
  //   addTimeslot(timeslots, { ...getTimeslot(9, 0, 60 * 6), message: 'Parent-child tournament' })
  // }
  if (isDate(date, JULY, 12)) {
    removeTimeslot(timeslots, _.range(9, 12))
    addTimeslot(timeslots, { ...getTimeslot(9, 0, 60 * 3), message: 'Wimbledon Social', heightOverride: 150 })
  }
  if (isDate(date, MAY, 30)) {
    removeTimeslot(timeslots, _.range(12 + 5, 12 + 9))
    addTimeslot(timeslots, { ...getTimeslot(12 + 5, 0, 60 * 1.5) })
    addTimeslot(timeslots, {
      ...getTimeslot(12 + 6, 30, 60 * 2.5),
      message: (
        <>
          Social Event:{' '}
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdHs3thqGXIKyjhUMPJvcuIce5VF9fKIB2LFefN0WavTHBw1w/viewform?usp=dialog"
            target="_blank"
            rel="noreferrer"
          >
            More info / Sign up
          </a>
        </>
      ),
      heightOverride: 150,
    })
  }
  if (isDate(date, JULY, 26)) {
    removeTimeslot(timeslots, _.range(10, 13))
    addTimeslot(timeslots, {
      ...getTimeslot(10, 0, 60 * 3),
      message: 'Junior Ice Cream Tournament',
    })
  }

  if (isDate(date, SEPT, 6)) {
    removeTimeslot(timeslots, _.range(10, 13))
    addTimeslot(timeslots, { ...getTimeslot(10, 0, 60 * 3), message: 'Junior Club Championships' })
  }

  // ##########################################################
  // if (isDate(date, SEPT, 14)) {
  //   removeTimeslot(timeslots, _.range(10, 15))
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(10, 0, 60 * 5),
  //     message: 'Club Championship Semi Finals - Singles',
  //     heightOverride: 300,
  //   })
  // }
  // if (isDate(date, SEPT, 15)) {
  //   removeTimeslot(timeslots, _.range(10, 15))
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(10, 0, 60 * 5),
  //     message: 'Club Championship Finals - Singles',
  //     heightOverride: 300,
  //   })
  // }
  // if (isDate(date, SEPT, 21)) {
  //   removeTimeslot(timeslots, _.range(10, 15))
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(10, 0, 60 * 5),
  //     message: 'Club Championship Semi Finals - Doubles',
  //     heightOverride: 300,
  //   })
  // }
  // if (isDate(date, SEPT, 22)) {
  //   removeTimeslot(timeslots, _.range(10, 15))
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(10, 0, 60 * 5),
  //     message: 'Club Championship Finals - Doubles',
  //     heightOverride: 300,
  //   })
  // }
  // ##########################################################

  if (isJuniorCampDay(date)) {
    removeTimeslot(timeslots, [9, 10, 11, 12])
    addTimeslot(timeslots, {
      ...getTimeslot(9, 0, 60 * 3.25),
      message: <Link to="/juniorcamps">Junior Summer Camps</Link>,
      heightOverride: 100,
    })
    addTimeslot(timeslots, getTimeslot(12, 15, 45))
  }

  if (isJuniorLessonsDay(date)) {
    removeTimeslot(timeslots, [16, 17, 18])
    removeTimeslot(timeslots, 18)
    removeTimeslot(timeslots, 16)
    addTimeslot(timeslots, getTimeslot(16, 0, 30))
    // addTimeslot(timeslots, getTimeslot(15, 0, 60 * 0.75))
    // addTimeslot(timeslots, getTimeslot(15, 45, 60 * 0.75))
    addTimeslot(timeslots, {
      ...getTimeslot(16, 30, 60 * 2),
      message: <Link to="/juniorlessons">Junior After-School Lessons</Link>,
      heightOverride: 100,
    })
    if (isWednesday(date) && isAdultLessonsDay(date)) {
    } else if (isThursday(date) && isAdultDoublesDay(date)) {
    } else {
      removeTimeslot(timeslots, 19)
      addTimeslot(timeslots, getTimeslot(18, 30, 60 * 1.5))
    }
  }

  if (isAdultDoublesDay(date) && !isDate(date, AUG, 4)) {
    if (isMonday(date)) {
      var isLongWeekend = isDate(date, AUG, 4) || isDate(date, SEPT, 1)
      removeTimeslot(timeslots, [17, 18, 19, 20, 21, 22])
      addTimeslot(timeslots, getTimeslot(17, 0, 90))
      addTimeslot(timeslots, getTimeslot(21, 30, 90))
      const novice3Courts = true
      // isDate(date, JULY, 15) || isDate(date, AUG, 19) || isDate(date, SEPT, 16) || isDate(date, SEPT, 23)
      addTimeslot(timeslots, {
        ...getTimeslot(18, 30, 90),
        span: novice3Courts ? 3 : isLongWeekend ? 1 : 2,
        message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
      })
      addTimeslot(timeslots, {
        ...getTimeslot(20, 0, 90),
        message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
        span: isLongWeekend ? 2 : 3,
      })
    } else if (!isSunday(date)) {
      removeTimeslot(timeslots, [17, 18, 19, 20, 21, 22])
      if (!isJuniorLessonsDay(date)) {
        // addTimeslot(timeslots, getTimeslot(18, 0, 30))
        addTimeslot(timeslots, getTimeslot(17, 0, 90))
      }
      addTimeslot(timeslots, getTimeslot(21, 30, 90))
      addTimeslot(timeslots, {
        ...getTimeslot(18, 30, 60 * 3),
        message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
        heightOverride: 100,
      })
    } else {
      removeTimeslot(timeslots, [17, 18, 19])
      addTimeslot(timeslots, getTimeslot(17, 0, 90))
      // var isLongWeekend = isDate(date, JUNE, 30) || isDate(date, AUG, 4) || isDate(date, SEPT, 1)
      addTimeslot(timeslots, {
        ...getTimeslot(18, 30, 60 * 1.5),
        message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
        span: isLongWeekend ? 2 : 3,
        heightOverride: 100,
      })
    }
  }

  // if (isDate(date, MAY, 20)) {
  //   removeTimeslot(timeslots, [17, 18, 19, 20, 21, 22])
  //   addTimeslot(timeslots, getTimeslot(17, 0, 90))
  //   addTimeslot(timeslots, getTimeslot(21, 30, 90))
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(18, 30, 90),
  //     span: 1,
  //     message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
  //   })
  //   addTimeslot(timeslots, {
  //     ...getTimeslot(20, 0, 90),
  //     span: 2,
  //     message: <Link to="/adult-doubles">Adult Social Doubles</Link>,
  //   })
  // }

  if (isDate(date, OCT, 5)) {
    removeTimeslot(timeslots, [20, 21, 22])
    addTimeslot(timeslots, getTimeslot(21, 30, 90))
    addTimeslot(timeslots, {
      ...getTimeslot(20, 0, 90),
      message: <Link to="/adult-doubles">Adult Social Doubles (intermediate only)</Link>,
      heightOverride: 100,
    })
  }

  if (isAdultLessonsDay(date)) {
    removeTimeslot(timeslots, [19, 20, 21, 22])
    removeTimeslot(timeslots, 18, 30)
    if (isJuniorLessonsDay(date)) {
      addTimeslot(timeslots, getTimeslot(18, 30, 45))
    } else {
      removeTimeslot(timeslots, 18)
      addTimeslot(timeslots, getTimeslot(18, 0, 75))
    }
    addTimeslot(timeslots, getTimeslot(21, 30))
    addTimeslot(timeslots, getTimeslot(22, 30))
    addTimeslot(timeslots, {
      ...getTimeslot(19, 15, 2 * 60 + 15),
      message: <Link to="/lessons">Adult Lessons</Link>,
      heightOverride: 100,
    })
  }

  if (isDate(date, AUG, 14)) {
    removeTimeslot(timeslots, [13, 14, 15])
    addTimeslot(timeslots, {
      ...getTimeslot(13, 0, 90),
      message: 'City of Cambridge Camp',
    })
    addTimeslot(timeslots, getTimeslot(14, 30, 90))
  }

  if (isSwingAndSplashDay(date)) {
    if (isDate(date, JULY, 15) || isDate(date, JULY, 22) || isDate(date, JULY, 29)) {
      // Both Swing and Splash + Cafe du Monde (overlapping)
      removeTimeslot(timeslots, [13, 14])
      addTimeslot(timeslots, {
        ...getTimeslot(13, 0, 2 * 60),
        message: 'Swing & Splash / Cafe du Monde Camps',
        heightOverride: 100,
      })
    } else {
      removeTimeslot(timeslots, [13, 14])
      addTimeslot(timeslots, {
        ...getTimeslot(13, 0, 2 * 60),
        message: 'Swing & Splash Camp',
        heightOverride: 100,
      })
    }
  }
  if (isHVNADay(date)) {
    removeTimeslot(timeslots, 12, 15) // 12:15pm
    removeTimeslot(timeslots, 13, 0) // 1:00pm
    addTimeslot(timeslots, {
      ...getTimeslot(12, 15, 105),
      message: 'HVNA Camp',
      heightOverride: 100,
    })
  }

  if (isDate(date, JULY, 14) || isDate(date, JULY, 21) || isDate(date, JULY, 28)) {
    removeTimeslot(timeslots, 14)
    addTimeslot(timeslots, {
      ...getTimeslot(14),
      message: 'Cafe du Monde Camp',
    })
  }

  if (isJuniorAdvCampDay(date)) {
    removeTimeslot(timeslots, [17, 18])
    let duration = 60 * 1.5
    addTimeslot(timeslots, {
      ...getTimeslot(17, 0, duration),
      span: 2,
      message: <Link to="/junioradvancedlessons">Advanced Junior Lessons</Link>,
    })
    addTimeslot(timeslots, getTimeslot(18, 30, 45))
  }

  if (isJuniorDropInDay(date)) {
    removeTimeslot(timeslots, 17)
    removeTimeslot(timeslots, 18)
    addTimeslot(timeslots, {
      ...getTimeslot(17, 0, 1 * 60),
      message: <Link to="/juniordropins">Junior Drop-Ins</Link>,
      span: 2,
    })
    addTimeslot(timeslots, {
      ...getTimeslot(18, 0, 1 * 60),
      message: <Link to="/juniordropins">Junior Drop-Ins</Link>,
      span: 2,
    })
  }

  // if (isAdultDropInDay(date) ) {
  //   if (isDate(date, OCT, 1)) {
  //     removeTimeslot(timeslots, [18, 19])
  //     addTimeslot(timeslots, {
  //       ...getTimeslot(18, 0, 2 * 60),
  //       message: <Link to="/dropins">Adult intermediate / advanced singles drop-in</Link>,
  //       heightOverride: 100,
  //     })
  //   } else {
  //     removeTimeslot(timeslots, [17, 18, 19])
  //     addTimeslot(timeslots, {
  //       ...getTimeslot(17, 0, 3 * 60),
  //       message: <Link to="/dropins">Adult intermediate / advanced singles drop-in</Link>,
  //       heightOverride: 100,
  //     })
  //   }
  // }

  if (isInterurbanHomeDate(date)) {
    removeTimeslot(timeslots, [18, 19, 20, 21, 22])
    removeTimeslot(timeslots, 18, 30)
    // 6:30pm start if there are after-school lessons; 6pm start otherwise.
    const startTime = isJuniorLessonsDay(date) ? [18, 30] : [18, 0]
    let desc = isDate(date, AUG, 12) ? 'Interurban finals' : 'Interurban'
    addTimeslot(timeslots, {
      ...getTimeslot(...startTime, 3 * 60),
      message: <Link to="/interurban">{desc}</Link>,
    })
  }

  return timeslots
}

const DEFAULTS = {
  0: DEFAULT_WEEKEND, // Sunday
  1: DEFAULT_WEEKDAY,
  2: DEFAULT_WEEKDAY,
  3: DEFAULT_WEEKDAY,
  4: DEFAULT_WEEKDAY,
  5: DEFAULT_WEEKDAY,
  6: DEFAULT_WEEKEND, // Saturday
}

const sortTimeslots = (timeslots) => _.sortBy(timeslots, 'startTime')

export const getStartTimes = (date) => {
  if (!date) {
    return []
  }
  let timeslots = getTimeslots(DEFAULTS[getDay(date)])
  timeslots = adjustTimeslots(date, timeslots)
  timeslots = sortTimeslots(timeslots)

  const result = []
  for (const timeslot of timeslots) {
    const startTime = timeslot.startTime
    const newDate = set(new Date(), {
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hours: startTime.getHours(),
      minutes: startTime.getMinutes(),
      seconds: 0,
      milliseconds: 0,
    })
    result.push({ ...timeslot, startTime: newDate })
  }
  return result
}

export const getStartTimeAsDate = (booking) => (booking ? parseISO(booking.startTime) : null)

export const mayBeCancelled = (booking, user) => {
  if (!booking || !booking.createdByUser || !user) {
    return false
  }
  const createdByUser = booking.createdByUser.id === user.id
  const bookingCancelCutoff = add(getStartTimeAsDate(booking), { minutes: CANCEL_BOOKING_GRACE_PERIOD })
  const isFutureBooking = isFuture(bookingCancelCutoff)

  return (createdByUser && isFutureBooking) || user.mayCancelAnyBooking
}

export const mayBeEdited = (booking, user) => {
  if (!booking || !booking.createdByUser || !user) {
    return false
  }
  const createdByUser = booking.createdByUser.id === user.id
  const bookingEditCutoff = add(getStartTimeAsDate(booking), { minutes: EDIT_BOOKING_GRACE_PERIOD })
  const isFutureBooking = isFuture(bookingEditCutoff)

  return (createdByUser && isFutureBooking) || user.mayEditAnyBooking
}

export const getLatestAllowedBooking = (user) => {
  if (user && user.mayIgnoreFutureBookingLimit) {
    return FAR_FUTURE
  } else {
    return add(set(new Date(), { hours: 23, minutes: 59, seconds: 59 }), { days: MAX_FUTURE_DAYS })
  }
}

export const mayBeBooked = (time, user, court) => {
  if (_.isNil(time) || !user) {
    return false
  }
  const earliestAllowed = add(time, { minutes: BOOKABLE_GRACE_PERIOD })
  return isFuture(earliestAllowed) && isBefore(time, getLatestAllowedBooking(user))
}

export const isLastMinute = (time) => {
  time = _.isDate(time) ? time : new Date(time)
  const lastMinuteAllowedInterval = {
    start: sub(new Date(), { minutes: BOOKABLE_GRACE_PERIOD }),
    end: add(new Date(), { minutes: LAST_MINUTE_BOOKING_PERIOD }),
  }
  const result = isWithinInterval(time, lastMinuteAllowedInterval, false)
  return result
}
